// src/pages/Auditorias/api.js
import axios from 'axios';

export const listarAuditorias = (filtros = {}) =>
  axios.get('/api/auditorias', { params: filtros });

export const listarAdvogados = () =>
  axios.get('/api/auditorias/advogados');

export const executarAuditoria = (payload) =>
  axios.post('/api/auditorias/executar', payload);

/**
 * Processos com movimento na DuraLex no intervalo, já cruzados com as auditorias
 * existentes.
 *
 * params: { since: 'YYYY-MM-DD', until?: 'YYYY-MM-DD', pagina?: number }
 * devolve: { processos: [...], paginacao: { pagina, total_paginas, por_pagina, total } }
 *
 * Uma página por chamada — a DuraLex tem rate-limit e são ~1600 processos.
 */
export const listarProcessosAlterados = (params) =>
  axios.get('/api/auditorias/processos-alterados', { params });

/** Apaga a auditoria e tudo o que lhe pertence. Irreversível. */
export const removerAuditoria = (id) =>
  axios.delete(`/api/auditorias/${id}`);

export const obterAuditoria = (id) =>
  axios.get(`/api/auditorias/${id}`);

export const obterProblemas = (id) =>
  axios.get(`/api/auditorias/${id}/problemas`);

export const obterParecer = (id) =>
  axios.get(`/api/auditorias/${id}/parecer`);

export const obterFicheiros = (id) =>
  axios.get(`/api/auditorias/${id}/ficheiros`);

export const obterConteudoPeca = (id, ficheiroId, campo = 'avaliacao') =>
  axios.get(`/api/auditorias/${id}/ficheiros/${ficheiroId}`, { params: { campo } });

export const obterArtefacto = (id, artefactoId) =>
  axios.get(`/api/auditorias/${id}/artefactos/${artefactoId}`);

export const validarExecucao = (id, execId) =>
  axios.post(`/api/auditorias/${id}/execucoes/${execId}/validar`);

export const cancelarAuditoria = (id) =>
  axios.post(`/api/auditorias/${id}/cancelar`);

export const guardarNota = (id, ficheiroId, nota) =>
  axios.post(`/api/auditorias/${id}/ficheiros/${ficheiroId}/nota`, { nota });

export const listarNotas = (id) =>
  axios.get(`/api/auditorias/${id}/notas`);

/** URL de download do PDF original no OneDrive (endpoint já existente) */
export const urlOriginalOneDrive = (caminho) =>
  axios.get('/api/onedrive/download-url', { params: { caminho } });

/** Os três prompts do motor + a lista de modelos, numa só chamada */
export const obterConfigPrompts = () =>
  axios.get('/api/auditorias/prompts');

/** payload: { texto_system, texto_user, modelo, reasoning_effort } */
export const guardarConfigPrompt = (origem, payload) =>
  axios.put(`/api/auditorias/prompts/${origem}`, payload);

/**
 * payload: { avaliar_peca_individual: boolean }
 *
 * Vale só para auditorias NOVAS. O modo é carimbado no mestre no primeiro
 * disparo e é imutável — mexer aqui não reescreve nenhuma auditoria já feita.
 */
export const guardarConfigDefinicoes = (payload) =>
  axios.put('/api/auditorias/definicoes', payload);

/**
 * Descarrega um .txt. O endpoint devolve texto simples com Content-Disposition,
 * mas o pedido tem de levar o header de autenticação — daí o blob em vez de
 * um <a href> directo.
 */
export const descarregarFicheiro = async (id, tipo, refId, nomeSugerido) => {
  const resposta = await axios.get(
    `/api/auditorias/${id}/download/${tipo}/${refId}`,
    { responseType: 'blob' }
  );

  guardarBlob(resposta.data, 'text/plain', nomeSugerido || 'auditoria.txt');
};

/**
 * Descarrega um ZIP: tipo = 'parecer' | 'processo'.
 *
 * Mesmo motivo do blob acima — o endpoint exige o header de autenticação, logo
 * não pode ser um <a href> directo. O tipo do Blob tem de ser application/zip:
 * reaproveitar o text/plain do irmão entrega um ficheiro que não abre.
 */
export const descarregarZip = async (id, tipo, nomeSugerido) => {
  let resposta;

  try {
    resposta = await axios.get(
      `/api/auditorias/${id}/download-zip/${tipo}`,
      { responseType: 'blob' }
    );
  } catch (e) {
    // Com responseType 'blob' o corpo de erro também chega em Blob. Sem isto,
    // um 404 legítimo ("ainda não há parecer") aparecia no ecrã como undefined.
    const corpo = e.response?.data;
    if (corpo instanceof Blob) {
      try {
        const { message } = JSON.parse(await corpo.text());
        if (message) e.mensagemLegivel = message;
      } catch {
        // corpo não era JSON — fica a mensagem original do axios
      }
    }
    throw e;
  }

  guardarBlob(resposta.data, 'application/zip', nomeSugerido || `auditoria-${tipo}.zip`);
};

/** Entrega um blob ao browser como download. */
function guardarBlob(dados, tipoMime, nome) {
  const url = window.URL.createObjectURL(new Blob([dados], { type: tipoMime }));
  const link = document.createElement('a');
  link.href = url;
  link.download = nome;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
